import { useEffect } from 'react';

interface ShippingPolicyModalProps {
  isOpen: boolean;
  onClose: () => void;
}

export default function ShippingPolicyModal({ isOpen, onClose }: ShippingPolicyModalProps) {
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [isOpen]);

  if (!isOpen) return null;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center"
      style={{ backgroundColor: 'rgba(0,0,0,0.6)' }}
      onClick={onClose}
    >
      <div
        className="bg-white rounded-2xl w-full max-w-lg mx-4 overflow-hidden shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="bg-[#F7F4F0] px-8 pt-8 pb-6 relative">
          <button
            onClick={onClose}
            className="absolute top-5 right-5 w-8 h-8 flex items-center justify-center rounded-full bg-black/10 hover:bg-black/20 transition-colors cursor-pointer"
          >
            <i className="ri-close-line text-[#3D2B1F] text-lg"></i>
          </button>
          <p className="text-xs font-medium tracking-widest text-[#8B7355] uppercase mb-2">Shipping Policy</p>
          <h2 className="text-2xl font-bold text-[#3D2B1F]">배송 정책</h2>
          <p className="text-xs text-[#8B7355] mt-2">시행일: 2025년 1월 1일 · 주식회사 위딘</p>
        </div>

        {/* Body */}
        <div className="px-8 py-7 max-h-[60vh] overflow-y-auto space-y-6">

          {/* 1. 배송비 안내 */}
          <div>
            <div className="flex items-center gap-2 mb-3">
              <div className="w-6 h-6 flex items-center justify-center">
                <i className="ri-money-dollar-circle-line text-[#8B7355] text-lg"></i>
              </div>
              <h3 className="text-base font-semibold text-[#3D2B1F]">1. 배송비 안내</h3>
            </div>
            <div className="pl-8 space-y-2">
              {[
                { label: '50,000원 이상 구매', value: '무료 배송' },
                { label: '50,000원 미만 구매', value: '3,000원' },
                { label: '제주도', value: '추가 3,000원' },
                { label: '도서·산간 지역', value: '추가 5,000원' },
              ].map(({ label, value }) => (
                <div key={label} className="flex items-center justify-between bg-[#F7F4F0] rounded-lg px-4 py-3">
                  <span className="text-sm text-[#5C4A3A]">{label}</span>
                  <span className="text-sm font-semibold text-[#8B7355]">{value}</span>
                </div>
              ))}
            </div>
            <div className="pl-8 mt-3">
              <div className="flex items-start gap-2 bg-[#8B7355]/10 rounded-lg px-4 py-3">
                <i className="ri-information-line text-[#8B7355] text-base mt-0.5 flex-shrink-0"></i>
                <p className="text-xs text-[#8B7355] leading-relaxed">
                  묶음 주문의 배송비는 최초 주문 기준으로 적용되며, 할인 쿠폰·적립금 사용 후의 실결제 금액을 기준으로 무료 배송 여부를 판단합니다.
                </p>
              </div>
            </div>
          </div>

          <div className="border-t border-[#EDE8E0]"></div>

          {/* 2. 배송 업체 */}
          <div>
            <div className="flex items-center gap-2 mb-3">
              <div className="w-6 h-6 flex items-center justify-center">
                <i className="ri-truck-line text-[#8B7355] text-lg"></i>
              </div>
              <h3 className="text-base font-semibold text-[#3D2B1F]">2. 배송 업체</h3>
            </div>
            <div className="pl-8 space-y-2 text-sm text-[#5C4A3A] leading-relaxed">
              <p>CJ대한통운을 기본 배송사로 이용하며, 재고 상황 및 지역에 따라 롯데택배·한진택배·로젠택배 중 하나로 자동 배정될 수 있습니다.</p>
              <p>배송사 선택은 당사 재량으로 결정되며, 특정 배송사 지정은 불가합니다.</p>
            </div>
          </div>

          <div className="border-t border-[#EDE8E0]"></div>

          {/* 3. 배송 기간 */}
          <div>
            <div className="flex items-center gap-2 mb-3">
              <div className="w-6 h-6 flex items-center justify-center">
                <i className="ri-time-line text-[#8B7355] text-lg"></i>
              </div>
              <h3 className="text-base font-semibold text-[#3D2B1F]">3. 배송 기간</h3>
            </div>
            <div className="pl-8 space-y-2">
              {[
                { label: '출고 소요일', value: '결제 확정 후 1–3 영업일' },
                { label: '배송 소요일 (수도권)', value: '출고 후 1–2 영업일' },
                { label: '배송 소요일 (지방)', value: '출고 후 2–3 영업일' },
                { label: '제주·도서·산간', value: '출고 후 3–5 영업일' },
              ].map(({ label, value }) => (
                <div key={label} className="flex items-center justify-between bg-[#F7F4F0] rounded-lg px-4 py-3">
                  <span className="text-sm text-[#5C4A3A]">{label}</span>
                  <span className="text-sm font-semibold text-[#8B7355]">{value}</span>
                </div>
              ))}
            </div>
            <div className="pl-8 mt-3 space-y-2 text-sm text-[#5C4A3A] leading-relaxed">
              <p>영업일 기준은 <strong className="text-[#3D2B1F]">월~금요일</strong>이며, 토·일요일 및 법정 공휴일, 당사 지정 휴무일은 제외됩니다.</p>
              <p>주문 폭주 기간(시즌 특가, 명절 전후 등)에는 출고 및 배송이 지연될 수 있으며, 이 경우 공지사항을 통해 안내드립니다.</p>
            </div>
          </div>

          <div className="border-t border-[#EDE8E0]"></div>

          {/* 4. 배송 조회 */}
          <div>
            <div className="flex items-center gap-2 mb-3">
              <div className="w-6 h-6 flex items-center justify-center">
                <i className="ri-map-pin-2-line text-[#8B7355] text-lg"></i>
              </div>
              <h3 className="text-base font-semibold text-[#3D2B1F]">4. 배송 조회</h3>
            </div>
            <div className="pl-8 space-y-2 text-sm text-[#5C4A3A] leading-relaxed">
              <p>출고 완료 후 등록된 이메일 또는 휴대폰 번호로 <strong className="text-[#3D2B1F]">송장번호</strong> 및 배송사 정보가 문자·이메일로 발송됩니다.</p>
              <p>각 배송사 홈페이지 또는 앱에서 송장번호를 입력하면 실시간 배송 현황을 확인할 수 있습니다.</p>
              <p>배송 조회 활성화에는 출고 후 최대 <strong className="text-[#3D2B1F]">24시간</strong>이 소요될 수 있습니다.</p>
            </div>
          </div>

          <div className="border-t border-[#EDE8E0]"></div>

          {/* 5. 배송지 변경 */}
          <div>
            <div className="flex items-center gap-2 mb-3">
              <div className="w-6 h-6 flex items-center justify-center">
                <i className="ri-edit-line text-[#8B7355] text-lg"></i>
              </div>
              <h3 className="text-base font-semibold text-[#3D2B1F]">5. 배송지 변경</h3>
            </div>
            <div className="pl-8 space-y-2 text-sm text-[#5C4A3A] leading-relaxed">
              <p>배송지 변경은 <strong className="text-[#3D2B1F]">출고 전</strong>에만 가능합니다. 출고 이후에는 배송지 변경이 불가하오니, 주문 즉시 배송지를 확인해 주세요.</p>
              <p>변경을 원하실 경우 고객센터(이메일: zeowithin@gmail.com)로 신속히 연락 주시기 바랍니다.</p>
              <p>출고 후 배송지 오류로 인한 반송·재발송 비용은 고객 부담입니다.</p>
            </div>
          </div>

          <div className="border-t border-[#EDE8E0]"></div>

          {/* 6. 오배송·파손·분실 처리 */}
          <div>
            <div className="flex items-center gap-2 mb-3">
              <div className="w-6 h-6 flex items-center justify-center">
                <i className="ri-error-warning-line text-[#8B7355] text-lg"></i>
              </div>
              <h3 className="text-base font-semibold text-[#3D2B1F]">6. 오배송·파손·분실 처리</h3>
            </div>
            <div className="pl-8 space-y-3 text-sm text-[#5C4A3A] leading-relaxed">
              <div>
                <p className="font-semibold text-[#3D2B1F] mb-1">오배송</p>
                <p>상품 수령 후 <strong className="text-[#3D2B1F]">7일 이내</strong>에 고객센터로 연락 주시면 정확한 상품으로 재발송해 드립니다. 재발송 배송비는 당사 부담입니다.</p>
              </div>
              <div>
                <p className="font-semibold text-[#3D2B1F] mb-1">배송 중 파손</p>
                <p>수령 시 외포장 및 내용물 상태를 확인하고, 파손이 확인될 경우 <strong className="text-[#3D2B1F]">사진 촬영 후 7일 이내</strong>에 고객센터로 접수해 주세요. 교환 또는 환불 처리해 드립니다.</p>
              </div>
              <div>
                <p className="font-semibold text-[#3D2B1F] mb-1">배송 분실</p>
                <p>배송 완료 처리 후 상품을 수령하지 못한 경우, 고객센터로 접수 주시면 배송사와 함께 조사 후 처리해 드립니다. 배송사 과실로 확인된 분실은 전액 보상됩니다.</p>
              </div>
            </div>
          </div>

          <div className="border-t border-[#EDE8E0]"></div>

          {/* 7. 배송 불가 지역 */}
          <div>
            <div className="flex items-center gap-2 mb-3">
              <div className="w-6 h-6 flex items-center justify-center">
                <i className="ri-map-pin-line text-[#8B7355] text-lg"></i>
              </div>
              <h3 className="text-base font-semibold text-[#3D2B1F]">7. 배송 불가 지역</h3>
            </div>
            <div className="pl-8 space-y-2 text-sm text-[#5C4A3A] leading-relaxed">
              <p>현재 <strong className="text-[#3D2B1F]">국내 배송만</strong> 지원합니다. 해외 배송은 현재 제공하지 않습니다.</p>
              <p>군부대(APO/FPO), 사서함(P.O. Box) 주소로는 배송이 불가합니다.</p>
              <p>일부 도서·산간 오지 지역은 배송 가능 여부를 사전에 고객센터로 문의해 주시기 바랍니다.</p>
            </div>
          </div>

          <div className="border-t border-[#EDE8E0]"></div>

          {/* 8. 고객센터 */}
          <div>
            <div className="flex items-center gap-2 mb-3">
              <div className="w-6 h-6 flex items-center justify-center">
                <i className="ri-customer-service-2-line text-[#8B7355] text-lg"></i>
              </div>
              <h3 className="text-base font-semibold text-[#3D2B1F]">8. 고객센터</h3>
            </div>
            <div className="pl-8 space-y-2">
              {[
                { label: '이메일', value: 'zeowithin@gmail.com' },
                { label: '운영 시간', value: '평일 10:00 – 17:00 (점심 12:00–13:00)' },
              ].map(({ label, value }) => (
                <div key={label} className="flex items-start justify-between bg-[#F7F4F0] rounded-lg px-4 py-3 gap-4">
                  <span className="text-sm text-[#5C4A3A] flex-shrink-0">{label}</span>
                  <span className="text-sm font-semibold text-[#8B7355] text-right">{value}</span>
                </div>
              ))}
            </div>
            <p className="pl-8 mt-3 text-xs text-[#8B7355]">
              주말·공휴일 접수된 문의는 다음 영업일에 순차적으로 답변 드립니다.
            </p>
          </div>

        </div>

        {/* Footer */}
        <div className="px-8 py-5 border-t border-[#EDE8E0]">
          <button
            onClick={onClose}
            className="w-full bg-[#8B7355] text-white text-sm font-medium py-3 rounded-xl hover:bg-[#7A6245] transition-colors duration-200 cursor-pointer whitespace-nowrap"
          >
            확인
          </button>
        </div>
      </div>
    </div>
  );
}
